/**
 * Toast Notification System
 *
 * Global toast notification manager
 * - Success, error, warning, info types
 * - Auto-dismiss with customizable duration
 * - Multiple toasts support
 * - Stacking and positioning
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import PropTypes from "prop-types";
import {
  FaCheckCircle,
  FaExclamationCircle,
  FaInfoCircle,
  FaTimesCircle,
} from "react-icons/fa";
import "./Toast.scss";

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);
  const timersRef = useRef({});

  const removeToast = useCallback((id) => {
    // Clear timer if it exists
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback(
    (message, type = "info", options = {}) => {
      const { duration = 5000, debugInfo = null } = typeof options === 'number' ? { duration: options } : options;
      const id = toastIdRef.current++;
      const toast = { id, message, type, duration, debugInfo };

      setToasts((prev) => [...prev, toast]);

      if (duration > 0) {
        // Store timer reference
        timersRef.current[id] = setTimeout(() => {
          removeToast(id);
        }, duration);
      }

      return id;
    },
    [removeToast]
  );

  // Memoize the context value so consumers (and the many hooks that list `toast`
  // in their effect deps) get a stable reference. Previously this object was
  // recreated on every render, re-subscribing Firestore listeners app-wide
  // whenever a toast appeared or auto-dismissed.
  const toast = useMemo(
    () => ({
      success: (message, options) => addToast(message, "success", options),
      error: (message, options) => addToast(message, "error", options),
      warning: (message, options) => addToast(message, "warning", options),
      info: (message, options) => addToast(message, "info", options),
    }),
    [addToast]
  );

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div
        className="toast-container"
        style={{
          position: "fixed",
          top: "20px",
          right: "20px",
          zIndex: 999999,
          pointerEvents: "none",
        }}
      >
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            debugInfo={toast.debugInfo}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

ToastProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

const Toast = ({ message, type, debugInfo, onClose }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    const debugText = JSON.stringify({
      message,
      timestamp: new Date().toISOString(),
      ...debugInfo,
    }, null, 2);
    navigator.clipboard.writeText(debugText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const colors = {
    success: { bg: "#f0fdf4", border: "#10b981", text: "#166534" },
    error: { bg: "#fef2f2", border: "#ef4444", text: "#991b1b" },
    warning: { bg: "#fffbeb", border: "#f59e0b", text: "#92400e" },
    info: { bg: "#eff6ff", border: "#3b82f6", text: "#1e40af" },
  };

  return (
    <div
      style={{
        background: colors[type].bg,
        borderLeft: `4px solid ${colors[type].border}`,
        color: colors[type].text,
        padding: "12px 16px",
        marginBottom: "8px",
        borderRadius: "6px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        display: "flex",
        alignItems: "center",
        gap: "12px",
        minWidth: "300px",
        maxWidth: "450px",
        pointerEvents: "all",
        fontSize: "14px",
        lineHeight: "1.4",
      }}
    >
      <div style={{ flex: 1 }}>{message}</div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        {debugInfo && (
          <button
            type="button"
            onClick={handleCopy}
            title="Copy error details for debugging"
            style={{
              background: copied ? colors[type].border : "transparent",
              border: `1px solid ${colors[type].border}`,
              color: copied ? "#fff" : colors[type].text,
              cursor: "pointer",
              fontSize: "11px",
              padding: "4px 8px",
              borderRadius: "4px",
              flexShrink: 0,
              fontWeight: 500,
              transition: "all 0.2s",
            }}
          >
            {copied ? '✓ Copied' : '📋 Copy Details'}
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            fontSize: "20px",
            color: "#9ca3af",
            padding: "0",
            width: "20px",
            height: "20px",
            flexShrink: 0,
            lineHeight: "1",
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
};

Toast.propTypes = {
  message: PropTypes.string.isRequired,
  type: PropTypes.oneOf(["success", "error", "warning", "info"]).isRequired,
  debugInfo: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};
